import "antd/dist/antd.css";
import "../App.css";

import { Link } from "react-router-dom";
import FaqAccordion from "../components/FaqAccordion";
import usePageMeta from "../usePageMeta";
import { jsonLdString } from "../jsonLd";

type FaqGroup = {
    heading: string;
    // `a` is the rich JSX rendered inside the accordion. `schemaAnswer`
    // is the plain-text version emitted into the FAQPage JSON-LD so
    // Google, ChatGPT web search and Perplexity can extract passages
    // without walking React nodes. Keep them semantically in sync.
    items: { q: string; a: React.ReactNode; schemaAnswer: string }[];
};

export const faqGroups: FaqGroup[] = [
    {
        heading: "Getting started",
        items: [
            {
                q: "What is Planamoto?",
                a: (
                    <p style={{ margin: 0 }}>
                        Planamoto is a tour planner for motorbike trips with mates.
                        It keeps your daily route, weather, shared expenses, and
                        checklists in one place so everyone sees the
                        same plan.
                    </p>
                ),
                schemaAnswer:
                    "Planamoto is a tour planner for group motorbike trips. It keeps your daily route, weather, shared expenses, and checklists in one place so everyone sees the same plan.",
            },
            {
                q: "Do I need to install an app?",
                a: (
                    <>
                        <p style={{ margin: "0 0 12px 0" }}>
                            No. Planamoto runs in the browser on your phone,
                            tablet, or laptop. Sign in, plan, and bring it
                            with you on the road.
                        </p>
                        <p style={{ margin: "0 0 8px 0" }}>
                            If you'd like a one-tap icon on your home screen
                            that opens Planamoto full-screen (no browser bar),
                            you can add it in seconds:
                        </p>
                        <p style={{ margin: "0 0 6px 0" }}>
                            <strong>iPhone / iPad (Safari):</strong> open{" "}
                            <a href="https://www.planamoto.com">planamoto.com</a>,
                            tap the Share button (the square with the upward
                            arrow), then choose <em>Add to Home Screen</em>{" "}
                            and tap <em>Add</em>.
                        </p>
                        <p style={{ margin: 0 }}>
                            <strong>Android (Chrome):</strong> open{" "}
                            <a href="https://www.planamoto.com">planamoto.com</a>,
                            tap the ⋮ menu in the top-right, then choose{" "}
                            <em>Install app</em> (or <em>Add to Home screen</em>{" "}
                            on older versions) and confirm.
                        </p>
                    </>
                ),
                schemaAnswer:
                    "No. Planamoto runs in the browser on your phone, tablet, or laptop. If you want a one-tap icon: on iPhone or iPad in Safari, open planamoto.com, tap Share, then Add to Home Screen. On Android in Chrome, open planamoto.com, tap the three-dot menu, then Install app.",
            },
            {
                q: "How much does it cost?",
                a: (
                    <p style={{ margin: 0 }}>
                        Planamoto is free. If paid features are added later,
                        anything you've already planned stays yours.
                    </p>
                ),
                schemaAnswer:
                    "Planamoto is free. If paid features are added later, anything you have already planned stays yours.",
            },
            {
                q: "Is it only for motorbikes?",
                a: (
                    <p style={{ margin: 0 }}>
                        It's built around the way riders plan (daily legs,
                        weather, pre-ride checks), but it works fine for any group
                        road trip, camper-van tour, or cycling holiday.
                    </p>
                ),
                schemaAnswer:
                    "No. Planamoto is built around how motorbike riders plan — daily legs, weather, pre-ride checks — but works for any group road trip, camper-van tour, or cycling holiday.",
            },
        ],
    },
    {
        heading: "Riding together",
        items: [
            {
                q: "How do I invite people to my tour?",
                a: (
                    <p style={{ margin: 0 }}>
                        Each tour gets a short join code. Share it with your
                        riding partners and they can join from the Tours screen
                        once they've signed up.
                    </p>
                ),
                schemaAnswer:
                    "Each tour gets a short join code. Share it with your riding partners and they can join from the Tours screen once they have signed up.",
            },
            {
                q: "Does everyone see the same plan?",
                a: (
                    <p style={{ margin: 0 }}>
                        Yes. Itinerary, expenses, and group checklists are shared
                        live across the whole group. Personal checklists stay
                        private to you.
                    </p>
                ),
                schemaAnswer:
                    "Yes. The itinerary, expenses, and group checklists are shared live across the whole group. Personal checklists stay private to you.",
            },
            {
                q: "Can I remove someone from a tour?",
                a: (
                    <p style={{ margin: 0 }}>
                        The tour owner can remove or re-add members at any
                        stage. The shared expenses they entered stay on the
                        tour so the settle-up still balances.
                    </p>
                ),
                schemaAnswer:
                    "Yes. The tour owner can remove or re-add members at any stage. Shared expenses they entered stay on the tour so the settle-up still balances.",
            },
        ],
    },
    {
        heading: "Expenses and currency",
        items: [
            {
                q: "Can I log expenses in different currencies?",
                a: (
                    <p style={{ margin: 0 }}>
                        Yes. Log each cost in the currency you actually paid.
                        Planamoto converts everything to the tour currency at
                        the end so the settle-up works no matter how many
                        borders you crossed.
                    </p>
                ),
                schemaAnswer:
                    "Yes. Log each cost in the currency you actually paid. Planamoto converts everything to the tour currency at the end so the settle-up works no matter how many borders you crossed.",
            },
            {
                q: "How does the settle-up work?",
                a: (
                    <p style={{ margin: 0 }}>
                        At the end of the tour, Planamoto totals everyone's
                        contributions and produces the smallest set of
                        payments needed to square the group up.
                    </p>
                ),
                schemaAnswer:
                    "At the end of the tour, Planamoto totals every rider's contributions and produces the smallest set of payments needed to square the group up.",
            },
            {
                q: "Where do the exchange rates come from?",
                a: (
                    <p style={{ margin: 0 }}>
                        Live rates come from a third-party feed. They're a
                        good guide for splitting receipts, but your bank's
                        actual rate on the day may differ slightly.
                    </p>
                ),
                schemaAnswer:
                    "Live rates come from a third-party feed. They are a good guide for splitting receipts, but your bank's actual rate on the day may differ slightly.",
            },
        ],
    },
    {
        heading: "On the road",
        items: [
            {
                q: "Does Planamoto work offline?",
                a: (
                    <p style={{ margin: 0 }}>
                        Not in real time, but you can export the whole tour
                        as a printable document and keep a paper copy in the
                        tank bag for when signal drops out.
                    </p>
                ),
                schemaAnswer:
                    "Not in real time, but you can export the whole tour as a printable document and keep a paper copy in the tank bag for when signal drops.",
            },
            {
                q: "Where does the weather come from?",
                a: (
                    <p style={{ margin: 0 }}>
                        Forecasts are pulled from a third-party weather
                        provider for each day's route. Always sanity-check
                        the weather against local sources before you set off.
                    </p>
                ),
                schemaAnswer:
                    "Forecasts are pulled from a third-party weather provider for each day's route. Always sanity-check the weather against local sources before you set off.",
            },
            {
                q: "Can I use it for route navigation?",
                a: (
                    <p style={{ margin: 0 }}>
                        Planamoto plans the tour: what you're doing each
                        day, where you're staying, what it costs. Turn-by-turn
                        navigation happens in your mapping app — every stop and
                        route link opens in Google Maps, Apple Maps or Waze,
                        which you set in your profile settings.
                    </p>
                ),
                schemaAnswer:
                    "Planamoto plans the tour — what you're doing each day, where you're staying, what it costs — and hands turn-by-turn navigation to your mapping app. Every stop and route link opens in Google Maps, Apple Maps or Waze, chosen in your profile settings.",
            },
        ],
    },
    {
        heading: "Account and data",
        items: [
            {
                q: "Can I delete my account?",
                a: (
                    <p style={{ margin: 0 }}>
                        Yes. Head to Profile and you can delete your
                        account at any time. Tours you own are removed; tours
                        you joined keep going for the rest of the group
                        without you.
                    </p>
                ),
                schemaAnswer:
                    "Yes. Head to Profile to delete your account at any time. Tours you own are removed; tours you joined keep going for the rest of the group without you.",
            },
            {
                q: "How do you handle my data?",
                a: (
                    <p style={{ margin: 0 }}>
                        Only what's needed to run the Service. See the{" "}
                        <Link to="/privacy">Privacy policy</Link> for the
                        full breakdown.
                    </p>
                ),
                schemaAnswer:
                    "Only what is needed to run the Service. See the Privacy policy at planamoto.com/privacy for the full breakdown.",
            },
            {
                q: "How do I report a bug or request a feature?",
                a: (
                    <p style={{ margin: 0 }}>
                        Email{" "}
                        <a href="mailto:support@planamoto.com">
                            support@planamoto.com
                        </a>. I read every message that comes in.
                    </p>
                ),
                schemaAnswer:
                    "Email support@planamoto.com. I read every message that comes in.",
            },
        ],
    },
];

function FAQ() {
    usePageMeta({
        title: "FAQ — Planamoto",
        description:
            "Common questions about Planamoto: tour codes, expenses, multi-currency, offline export, and how a group of riders plan together.",
        url: "https://www.planamoto.com/faq",
    });
    // Google retired FAQ rich results in May 2026, so this block is
    // kept for AI/LLM citation surfaces (AI Overviews, Perplexity,
    // ChatGPT search) rather than SERP snippets. Answers use the plain
    // `schemaAnswer` string on each item, not the JSX in `a`.
    const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqGroups.flatMap((g) =>
            g.items.map((it) => ({
                "@type": "Question",
                name: it.q,
                acceptedAnswer: { "@type": "Answer", text: it.schemaAnswer },
            })),
        ),
    };
    const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.planamoto.com" },
            { "@type": "ListItem", position: 2, name: "FAQ", item: "https://www.planamoto.com/faq" },
        ],
    };
    return (
        <article className="gt-faq-page">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(faqLd) }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(breadcrumbLd) }}
            />
            <div className="gt-eyebrow">Help</div>
            <h1 className="gt-h1 gt-faq-page__title">Frequently asked questions</h1>
            <p className="gt-faq-page__lede">
                Quick answers to the things riders ask most. Can't find what
                you're looking for? Email{" "}
                <a href="mailto:support@planamoto.com">
                    support@planamoto.com
                </a>
                .
            </p>

            {faqGroups.map((group) => (
                <section key={group.heading} className="gt-faq-page__group">
                    <h2 className="gt-h3 gt-faq-page__group-heading">
                        {group.heading}
                    </h2>
                    <FaqAccordion items={group.items} idPrefix={group.heading} />
                </section>
            ))}

            <div className="gt-faq-page__back">
                <Link to="/">← Back to home</Link>
            </div>
        </article>
    );
}

export default FAQ;
